import axios from "utils/axiosConfig";
import { urlService } from "infra/envs";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { setCurrentCandidateAI } from "reducers/candidate/currentCandidateAISlice";
import { currentCandidate, updateCandidateVersion } from "reducers/candidate/currentCandidateSlice";
import CandidateService from "services/CandidateService";
import GeneralUtilityService from "services/GeneralUtilityService";
import SocialMediaURL from "services/SocialMediaURL.service";
import { updateEducation } from "reducers/candidate/educationsSlice";
import { updateExperience } from "reducers/candidate/experiencesSlice";
import { enqueueSnackbar } from "notistack";
import { AI_PARSE_TIMEOUT_DURATION } from "constants/aiParse";

const mapDataForSubmission = (formData) => {
  const p = formData.personal ?? {};
  const prefs = formData.personalDetailsPreferences ?? {};
  const prefsFlattened = {
    ...prefs.compensation, 
    ...prefs.lookingFor, 
    ...prefs.contact,
    ...prefs.personalInformation,
    ...prefs.personality
  }
  
  return {
    information: {
      firstName: p.firstName ?? null,
      lastName: p.lastName ?? null,
      headline: p.headline ?? null,
      dateOfBirth: p.dateOfBirth ?? null,
      email: p.email ?? null,
      phone: p.phone || null,
      linkedin: SocialMediaURL.extractLinkedinId(p.linkedin) ?? null,
      countryCodeId: p.calling_code_id?.id ?? null,
      militaryObligation: p.military_obligations == null ? null : Boolean(p.military_obligations),
      deferMilitaryUntil: p.defer_until ?? null,
      anonymousReport: p.anonymousReport ?? null,
      location: p.location
    },

    preferences: {
      currencyId: prefsFlattened.currencyId?.id ?? null,
      compensations: (prefsFlattened.compensations ?? []).map(x => x.id),

      originalMinMonthlyNetSalary: prefsFlattened.netMonthlySalary ? Number(prefsFlattened.netMonthlySalary) : null,
      originalMinAnnualGrossSalary: prefsFlattened.grossAnnualSalary ? Number(prefsFlattened.grossAnnualSalary) : null,
      noticePeriod: prefsFlattened.noticePeriod ? Number(prefsFlattened.noticePeriod) : null,
      availability: prefsFlattened.availability ? `${prefsFlattened.availability}-01` : null,

      attitudeId: prefsFlattened.attitude?.id ?? null,
      motivations: (prefsFlattened.motivations ?? []).map(x => x.id),
      softSkills: (prefsFlattened.softSkills ?? []).map(x => x.id),
      hardSkills: (prefsFlattened.hardSkills ?? []).map(x => x.id),

      contactFrequencyId: prefsFlattened.contactFrequency?.id ?? null,
      contactTypes: (prefsFlattened.meansOfContact ?? []).map(x => x.id),
      workingModels: (prefsFlattened.workingModels ?? []).map(x => x.id),
      jobTypes: (prefsFlattened.jobTypes ?? []).map(x => x.id),

      industries: (prefsFlattened.industries ?? []).map(x => x.id),
      jobFunctions: (prefsFlattened.jobFunctions ?? []).map(x => x.id),
      occupations: (prefsFlattened.occupations ?? []).map(x => x.id),

      companySizes: (prefsFlattened.companySizes ?? []).map(x => x.id),
      vehicles: (prefsFlattened.vehicles ?? []).map(x => x.id),
      gender: prefsFlattened.gender?.id ?? null,
      languages: (prefsFlattened.languages ?? []).map(lang => ({
        languageId: lang?.language?.id ?? null,
        languageLevelId: lang?.language_level?.id ?? null,
      })),
      relocations: prefsFlattened.relocation ?? null 
    },

    experiences: (formData.experiences ?? []).map(exp => ({
      companyId: exp.company?.id ?? null,
      jobTitle: exp.jobTitle ?? null,
      jobDescription: exp.jobDescription ?? null,
      startAt: exp.startAt ?? null,
      
      endAt: exp.endAt ?? null,
      teamSize: exp.teamSize ?? null,
      industries: (exp.industries ?? []).map(x => x.id),
      skills: (exp.skills ?? []).map(x => x.id),
      responsibilities: (exp.responsibilities ?? []).map(x => x.id),
      jobFunctions: (exp.job_functions ?? []).map(x => x.id),
      location: exp.location
        ? {
            coordinates: {
              latitude: Number(exp.location.latitude),
              longitude: Number(exp.location.longitude),
            },
            locationInfo: {
              country: {
                name: exp.location.country?.name ?? null,
                isoAlpha3Code: exp.location.country?.isoAlpha3Code ?? null,
              },
              state: exp.location.state?.name ?? null,
              county: exp.location.county?.name ?? null,
              city: exp.location.city?.name ?? null,
              district: exp.location.district?.name ?? null,
              postalCode: exp.location.postalCode?.code ?? null,
            },
          }
        : null,
        jobChangeReasonId: exp.jobChangeReason?.id ?? null,
        jobTypeId: exp.jobType?.id ?? null,
      occupations: (exp.occupations ?? []).map(x => x.id),
    })),

    educations: (formData.educations ?? []).map(edu => ({
      universityId: edu.university?.id ?? null,
      departmentId: edu.department?.id ?? null,
      educationLevelId: edu.educationLevel?.id ?? null,
      grade: edu.grade ? parseFloat(edu.grade).toFixed(1) : null,
      startAt: edu.start_at ?? null,
      endAt: edu.end_at ?? null,
      academicFields: (edu.academic_fields ?? []).map(x => x.id),
    })),
  };
};

const GENERIC_VALIDATION_ERROR_MESSAGE = "The given data was invalid.";

const extractSubmitErrorMessages = (error) => {
  const errorData = error?.response?.data;

  if (!errorData) {
    return ["Unable to submit candidate. Please try again."];
  }

  if (typeof errorData === "string") {
    return [errorData];
  }

  const messages = [];

  const collectMessages = (value) => {
    if (!value) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(collectMessages);
      return;
    }

    if (typeof value === "string") {
      messages.push(value);
      return;
    }

    if (typeof value === "object") {
      Object.values(value).forEach(collectMessages);
    }
  };

  if (typeof errorData?.message === "string") {
    messages.push(errorData.message);
  }

  collectMessages(errorData?.errors);

  if (!messages.length) {
    const fallbackMessage = GeneralUtilityService.extractErrorMessage(errorData);

    if (fallbackMessage) {
      messages.push(fallbackMessage);
    }
  }

  const dedupedMessages = [...new Set(messages.filter(Boolean))];

  if (!dedupedMessages.length) {
    return ["Unable to submit candidate. Please try again."];
  }

  if (dedupedMessages.length > 1) {
    return dedupedMessages.filter(
      (message) => message !== GENERIC_VALIDATION_ERROR_MESSAGE
    );
  }

  return dedupedMessages;
};

export default function useParseAi() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const [submitErrorMessages, setSubmitErrorMessages] = useState([]);
  const dispatch = useDispatch();

  const getParsedAIDataFromLocalStorage = useCallback((candidateID) => {

    const data = localStorage.getItem(`currentCandidateAI-${candidateID}`);
    if (!data) return null;
    setIsSubmitSuccess(true);
    return dispatch(setCurrentCandidateAI(JSON.parse(data)));
  }, [dispatch]);

  const parseAiData = useCallback(async (candidateID) => {
    if (!candidateID) return;

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${urlService.AI_PARSE_PATH}/${candidateID}`
      );
      const mappedData = CandidateService.mapCandidateAIData(response.data);
      dispatch(setCurrentCandidateAI(mappedData));
      localStorage.setItem(`currentCandidateAI-${candidateID}`, JSON.stringify(mappedData));
      enqueueSnackbar("Candidate data parsed with AI successfully.", {
        variant: "success",
        autoHideDuration: 5000
      });
      setIsSubmitSuccess(true);
      return mappedData;
    } catch (error) {
      console.error("Error parsing AI data:", error);
      setIsSubmitSuccess(false);
      enqueueSnackbar(
        "Error parsing candidate data with AI. Please try again.", {
            variant: "error",
            autoHideDuration: 5000
        })
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const submitAiData = useCallback(async (candidateID, aiData) => {
    if (!candidateID || !aiData) return;

    setIsSubmitSuccess(false);
    setIsSubmitLoading(true);
    setSubmitErrorMessages([]);

    try {
      const mappedDataForSubmission = mapDataForSubmission(aiData);
      
      const response = await axios.post(
        urlService.AI_PARSE_SUBMIT(candidateID),
        mappedDataForSubmission
      ,{
        timeout: AI_PARSE_TIMEOUT_DURATION
      });
      dispatch(currentCandidate(CandidateService.mapCandidateData(response.data?.candidate)));
      dispatch(updateEducation(response.data?.educations));
      dispatch(updateCandidateVersion());
      dispatch(updateExperience(response.data?.experiences));
      localStorage.removeItem(`currentCandidateAI-${candidateID}`);
      setIsSubmitSuccess(true);
      return true;
    } catch (error) {
      console.error("Error submitting AI data:", error);
      setIsSubmitSuccess(false);
      const errorMessages = extractSubmitErrorMessages(error);
      setSubmitErrorMessages(errorMessages);
      enqueueSnackbar(errorMessages[0], {
        variant: "error",
        autoHideDuration: 5000
      });
      return false;
    } finally {
      setIsSubmitLoading(false);
    }
  }, [dispatch]);

  return {
    parseAiData,
    submitAiData,
    isLoading,
    isSubmitLoading,
    isSubmitSuccess,
    submitErrorMessages,
    getParsedAIDataFromLocalStorage
  };
}
